import styled from '@emotion/styled'
import { Close } from 'app/components/Common/Close'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { Media } from 'app/theme/media'
import { AnimatePresence, motion } from 'framer-motion'
import React, { memo, useEffect, useRef, useState } from 'react'

import { Contacts } from './Contacts'
import { MenuItem, Props as MenuItemProps } from './MenuItem'

export interface Props {
  languageCode: string
  menuItems: MenuItemProps[]
  modalStatus?: boolean
  onClick?: any
  pageID?: string
}

export const Navigation = memo(function Navigation({
  languageCode,
  menuItems,
  modalStatus,
  onClick,
}: Props) {
  if (!menuItems) {
    return null
  }

  const mainRef = useRef<any>(null)

  const [currentIndex, setCurrentIndex] = useState<number>()
  const [submenuVisibility, setSubmenuVisibility] = useState<number>()

  useEffect(() => {
    if (modalStatus) {
      mainRef.current.focus()
    }

    const currentMenuItem = menuItems
      .map((c) => {
        if (typeof window === 'undefined') return

        return c.childs?.find((i) => window.location.pathname === i.url)
      })
      .findIndex((i) => i !== undefined)

    setCurrentIndex(currentMenuItem)
  }, [modalStatus])

  return (
    <Container data-lenis-prevent ref={mainRef} tabIndex={-1}>
      <AnimatePresence mode="wait">
        {modalStatus ? (
          <>
            <Background
              key="navigation"
              initial={{ clipPath: 'inset(100% 0 0 0)' }}
              animate={{ clipPath: 'inset(0%)' }}
              exit={{ clipPath: 'inset(100% 0 0 0)' }}
              transition={{
                ease: [0.87, 0, 0.13, 1],
                duration: 1,
              }}
            />

            <Frame
              key="frame"
              initial={{ clipPath: 'inset(100% 0 0 0)' }}
              animate={{
                clipPath: 'inset(0%)',
                transition: {
                  duration: 0.5,
                  delay: 0.5,
                },
              }}
              exit={{
                clipPath: 'inset(0% 0% 100%)',
                transition: {
                  duration: 0.6,
                },
              }}
              transition={{
                ease: [0.87, 0, 0.13, 1],
                duration: 1,
              }}
            />
          </>
        ) : null}
      </AnimatePresence>

      <Close
        className={`close${modalStatus ? ' visible' : ''}`}
        languageCode={languageCode}
        onClick={onClick}
        position="L"
        variant="invert"
      />

      <Wrapper row stretch>
        <LeftSide>
          <Media lessThan="desktopSmall">
            <Nav role="navigation">
              <NavList>
                {menuItems.map((item, index) => (
                  <NavLi key={index}>
                    <MenuItem
                      {...item}
                      modalStatus={modalStatus}
                      onClick={() => {
                        if (submenuVisibility === index) {
                          setSubmenuVisibility(-1)
                        } else {
                          if (currentIndex === index) {
                            setSubmenuVisibility(-1)
                            setCurrentIndex(-1)
                          } else {
                            setSubmenuVisibility(index)
                            setCurrentIndex(-1)
                          }
                        }
                      }}
                      style={
                        modalStatus
                          ? {
                              transitionDelay: `${0.1 * index + 1}s`,
                              transform: 'none',
                            }
                          : {
                              transitionDelay: `${0.05 * index}s`,
                              transform: 'translateY(100%)',
                            }
                      }
                      submenuVisibility={
                        submenuVisibility === index || currentIndex === index
                      }
                    />
                  </NavLi>
                ))}
              </NavList>
            </Nav>
          </Media>

          <Media greaterThanOrEqual="desktopSmall">
            <Nav role="navigation">
              <NavList>
                {menuItems.slice(0, 3).map((item, index) => (
                  <NavLi key={index}>
                    <MenuItem
                      {...item}
                      modalStatus={modalStatus}
                      style={
                        modalStatus
                          ? {
                              transitionDelay: `${0.1 * index + 1}s`,
                              transform: 'none',
                            }
                          : {
                              transitionDelay: `${0.05 * index}s`,
                              transform: 'translateY(100%)',
                            }
                      }
                    />
                  </NavLi>
                ))}
              </NavList>
              <NavList>
                {menuItems.slice(3, 5).map((item, index) => (
                  <NavLi key={index}>
                    <MenuItem
                      {...item}
                      modalStatus={modalStatus}
                      style={
                        modalStatus
                          ? {
                              transitionDelay: `${0.1 * (index + 3) + 1}s`,
                              transform: 'none',
                            }
                          : {
                              transitionDelay: `${0.05 * (index + 3)}s`,
                              transform: 'translateY(100%)',
                            }
                      }
                    />
                  </NavLi>
                ))}
              </NavList>
              <NavList>
                {menuItems.slice(5).map((item, index) => (
                  <NavLi key={index}>
                    <MenuItem
                      {...item}
                      modalStatus={modalStatus}
                      style={
                        modalStatus
                          ? {
                              transitionDelay: `${0.1 * (index + 6) + 1}s`,
                              transform: 'none',
                            }
                          : {
                              transitionDelay: `${0.05 * (index + 6)}s`,
                              transform: 'translateY(100%)',
                            }
                      }
                    />
                  </NavLi>
                ))}
              </NavList>
            </Nav>
          </Media>

          <Contacts modalStatus={modalStatus} />
        </LeftSide>
      </Wrapper>
    </Container>
  )
})

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100svh;
  overflow-y: auto;
  position: absolute;
  top: 0;
  left: 0;

  .close {
    opacity: 0;
    transition: 0.8s 0s ease-in-out;
    &.visible {
      opacity: 1;
      transition-delay: 1s;
    }
  }
`

const Background = styled(motion.div)`
  width: 100%;
  height: 100svh;
  background: ${({ theme }) => theme.colors.variants.primaryLight};
  position: absolute;
  top: 0;
  left: 0;
  will-change: clip-path;
`

const Wrapper = styled(FlexBox)`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding-top: 5.25rem;
  -webkit-overflow-scrolling: auto;

  @media (max-width: 1199px) {
    display: block;
    padding-top: 4.5rem;
    z-index: 2;
  }
`

const LeftSide = styled.div`
  width: 60%;
  padding-left: 5vw;

  @media (max-width: 1439px) {
    width: 65%;
  }

  @media (max-width: 1199px) {
    width: 100%;
    padding: 0 2rem;
  }
`

const Nav = styled.nav`
  display: flex;
  width: 100%;
  padding-bottom: 3.75rem;

  @media (max-width: 1199px) {
    display: block;
  }
`

const NavList = styled.ul`
  margin-right: 10vw;
  &:last-of-type {
    margin-right: 0;
  }

  @media (max-width: 1199px) {
    width: 100%;
    margin-right: 0;
  }
`

const NavLi = styled.li`
  margin-top: 3.75rem;

  @media (max-width: 1199px) {
    margin-top: 0.75rem;
  }
`

const Frame = styled(motion.div)`
  width: 27.778vw;
  background: url('navigation.webp') no-repeat center;
  background-size: cover;
  clip-path: inset(100% 0 0 0);
  position: absolute;
  top: 9rem;
  right: 5vw;
  bottom: 4.875rem;

  @media (max-width: 1199px) {
    display: none;
  }
`
